// src/components/ShowCard.tsx
import { motion } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { useAppStore } from "../store/app.store";
import { ExampleExperience } from "../constants/example-experiences";

interface ShowCardProps {
  example: ExampleExperience;
  offset: number;
  isActive: boolean;
  onClick: (e: React.MouseEvent) => void;
  maxHeight?: number;
}

const ShowCard = ({ example, offset, isActive, onClick, maxHeight }: ShowCardProps) => {
  const [isClicked, setIsClicked] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const theme = useAppStore((state) => state.theme);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isActive) {
      video.currentTime = 0;
      void video.play().catch(() => {});
    } else {
      video.pause();
      video.currentTime = 0;
      setIsHovered(false);
    }
  }, [isActive]);

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth < 1280;

  const defaultMobileHeight = 350;
  const defaultTabletHeight = 480;
  const defaultDesktopHeight = 500;

  const height =
    maxHeight ||
    (isMobile ? defaultMobileHeight : isTablet ? defaultTabletHeight : defaultDesktopHeight);

  let width: number;
  if (isMobile) {
    width = Math.min(height * (2 / 3), windowWidth * 0.7);
  } else if (isTablet) {
    width = Math.min(height * (2 / 3), windowWidth * 0.55);
  } else {
    width = Math.min(height * (2 / 3), windowWidth * 0.4);
  }

  const radius = height * (isMobile ? 1 : isTablet ? 1.2 : 1.4);
  const baseAngleStep = isMobile ? 28 : 24;
  const expansionFactor = 1 - Math.min(Math.abs(offset) * 0.05, 0.2);
  const angle = offset * baseAngleStep * expansionFactor;
  const rad = (angle * Math.PI) / 180;
  const x = Math.sin(rad) * radius;
  const z = (Math.cos(rad) - 1) * radius;

  const liftPerStep = height * 0.06;
  const liftAmount = Math.abs(offset) * liftPerStep;
  const activeCardBoost = height * 0.08;
  const y = isActive ? -activeCardBoost : -liftAmount;

  const rotateY = angle * 0.6;
  const zIndex = Math.round(1000 - Math.abs(offset) * 10);
  const distanceScale = 1 - Math.abs(offset) * 0.08;

  const showOverlay = isActive && isHovered && !isMobile;

  const handleInternalClick = (e: React.MouseEvent) => {
    if (isActive) {
      setIsClicked(true);
      setTimeout(() => onClick(e), 150);
    } else {
      onClick(e);
    }
  };

  return (
    <motion.div
      layoutId={`card-${example.id}`}
      onClick={handleInternalClick}
      onHoverStart={() => {
        if (isActive && !isMobile) setIsHovered(true);
      }}
      onHoverEnd={() => {
        setIsHovered(false);
      }}
      style={{
        width,
        height,
        zIndex,
        transformStyle: "preserve-3d",
        perspective: "1000px",
      }}
      whileTap={isActive ? { scale: 0.95, z: 200 } : {}}
      animate={{
        x,
        y,
        z: isActive ? 150 : z,
        rotateY,
        rotateX: isClicked ? 360 : 0,
        scale: isActive ? 1.05 : Math.max(distanceScale, 0.7),
        opacity: Math.abs(angle) > 110 ? 0 : 1,
        filter: isActive
          ? "brightness(1) blur(0px)"
          : `brightness(${1 - Math.abs(offset) * 0.2}) blur(${
              Math.abs(offset) > 1.5 ? "2px" : "0px"
            })`,
      }}
      transition={{
        rotateX: { duration: 0.6, ease: "easeInOut" },
        default: { type: "spring", stiffness: 160, damping: 22 },
      }}
      className={`absolute cursor-pointer rounded-[24px] md:rounded-[32px] overflow-hidden shadow-2xl border transition-colors duration-300 ${
        theme === "dark" ? "border-white/10 bg-zinc-900" : "border-primary/20 bg-white"
      }`}
    >
      {isActive ? (
        <video
          ref={videoRef}
          src={example.videoUrl}
          poster={example.posterUrl}
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        />
      ) : (
        <motion.img
          layoutId={`image-${example.id}`}
          src={example.posterUrl}
          alt={example.title}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        />
      )}

      <motion.div
        animate={{
          opacity: showOverlay ? 1 : 0,
        }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none"
      />

      <motion.div
        animate={{
          opacity: showOverlay ? 1 : 0,
          y: showOverlay ? 0 : 10,
        }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="absolute bottom-0 p-5 md:p-6 w-full pointer-events-none"
      >
        <p className="text-[10px] md:text-[11px] lg:text-sm text-primary font-bold uppercase tracking-widest mb-1">
          {example.eyebrow}
        </p>

        <h2 className="text-lg md:text-xl lg:text-2xl font-black tracking-tight leading-tight text-white uppercase">
          {example.title}
        </h2>

        <p className="mt-2 text-[11px] md:text-xs lg:text-sm text-white/70 leading-relaxed max-w-[90%]">
          {example.label}
        </p>
      </motion.div>
    </motion.div>
  );
};

export default ShowCard;
